import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { THREEx } from '@ar-js-org/ar.js-threejs'
import { ArToolkitContext } from '@ar-js-org/ar.js-threejs/types/ArToolkitContext';
import { ArToolkitSource } from '@ar-js-org/ar.js-threejs/types/ArToolkitSource';
import { Scene, WebGLRenderer } from 'three';
import { NgtCameraManual } from 'angular-three';

/**
 * AR initialization options
 * Matches AR.js ArToolkitContext and ArMarkerControls options
 */
export interface ArOptions {
  patternUrl?: string;        // ArMarkerControls - URL to pattern file
  patternRatio?: number;      // ArToolkitContext - Border/pattern ratio (0.5-0.95)
  size?: number;              // ArMarkerControls - Marker size in meters (larger = smaller scene)
  sceneScale?: number;        // Three.js Scene - Uniform scale multiplier (0.01-1, typically 0.05)
  smoothCount?: number;       // ArMarkerControls - Number of frames to smooth (1-30)
  smoothTolerance?: number;   // ArMarkerControls - Position tolerance (0.001-0.1)
  smoothThreshold?: number;   // ArMarkerControls - Threshold for smoothing (0-10)
}

/**
 * Device configuration for AR
 */
export interface ArDeviceConfig {
  isDesktop: boolean;
  browser: 'Firefox' | 'Chrome' | 'Safari' | 'Edge' | 'Unknown';
  containerElement?: HTMLElement; // Optional: container for AR video (Portal mode)
}

/**
 * Callbacks for AR lifecycle events
 * Decouples ArService from app-specific state management
 */
export interface ArCallbacks {
  /** Called when AR starts loading */
  onLoadingStart?: () => void;
  /** Called when AR finishes loading */
  onLoadingEnd?: () => void;
  /** Called when scene should be visible (marker detected) */
  onSceneVisible?: (visible: boolean) => void;
  /** Called when camera orientation is unsupported */
  onUnsupportedOrientation?: (unsupported: boolean) => void;
  /** Called when AR camera is enabled */
  onCameraEnabled?: (enabled: boolean) => void;
}

/**
 * AR bypass configuration
 * Replaces direct AppStore dependency
 */
export interface ArBypassConfig {
  /** Force bypass AR (for testing) */
  forceBypass?: boolean;
  /** Current campaign short code (for testing campaign detection) */
  campaignShortCode?: string;
  /** Enable development-only bypass checks */
  enableDevBypass?: boolean;
}

/**
 * AR Service - Shared AR.js integration for all apps
 *
 * **Phase E Refactor**: Fully decoupled from app-specific dependencies
 *
 * Provides AR marker detection and camera feed management using AR.js library.
 * Originally in libs/experience (app-specific) but moved to libs/shared to enable
 * AR preview in Portal and follow proper architecture (AR is rendering mode).
 *
 * **Architecture**:
 * - ✅ No UIService dependency (uses standard DOCUMENT injection)
 * - ✅ No AppStore dependency (uses callbacks for state updates)
 * - ✅ Configuration-based bypass logic (no direct store access)
 * - ✅ Optional device detection (passed via config)
 * - ✅ Fully standalone and reusable
 *
 * **Usage**:
 * ```typescript
 * const arService = inject(ArService);
 * const canvasStore = gameEngine.getCanvasStore();
 *
 * // Set callbacks for app-specific concerns
 * arService.setCallbacks({
 *   onLoadingStart: () => appStore.startLoading(),
 *   onLoadingEnd: () => appStore.stopLoading(),
 *   onSceneVisible: (visible) => updateUI(visible),
 *   onCameraEnabled: (enabled) => setCameraState(enabled)
 * });
 *
 * // Configure device info
 * arService.setDeviceConfig({
 *   isDesktop: deviceService.isDesktop(),
 *   browser: deviceService.getDeviceInfo().browser
 * });
 *
 * // Initialize AR
 * arService.initAR(
 *   canvasStore.scene,
 *   canvasStore.camera,
 *   canvasStore.gl,
 *   {
 *     patternUrl: 'path/to/pattern.patt',
 *     patternRatio: 0.75,
 *     smoothCount: 5,
 *     smoothTolerance: 0.01,
 *     smoothThreshold: 2
 *   }
 * );
 * ```
 *
 * @see Phase E: ArService migration to libs/shared
 * @see COMMIT_DEC_16_2025_PHASE_D5_TESTING.md
 */
@Injectable({
  providedIn: 'root'
})
export class ArService {
  // Standard Angular dependencies
  private document = inject(DOCUMENT);
  private window: Window & typeof globalThis;

  // AR.js components
  protected arToolkitSource?: ArToolkitSource;
  protected arToolkitContext?: ArToolkitContext;
  protected camera?: NgtCameraManual;
  protected renderer?: WebGLRenderer;
  protected scene?: Scene;

  // Saved initAR arguments — used to fully re-initialise after the browser
  // kills the camera stream (e.g. user switches app/tab on mobile)
  private lastInitParams?: {
    scene: Scene;
    camera: NgtCameraManual;
    renderer: WebGLRenderer;
    options?: ArOptions;
  };

  // Bound references kept so we can remove the listeners in destroy()
  private visibilityChangeListener?: () => void;
  private resizeListener?: () => void;

  // Camera init retry state (handles iOS hardware-hold after permission probe releases tracks)
  private initRetryCount = 0;
  private readonly MAX_INIT_RETRIES = 3;

  // Monotonically increasing generation counter.
  // Incremented at the start of every initAR() call AND inside pause()/destroy().
  // All async callbacks capture their generation at call-time and bail out if the
  // counter has advanced — this prevents a stale getUserMedia success/error callback
  // from re-enabling the camera after the caller has already called pause() or destroy().
  private initGeneration = 0;

  // Configuration (set by consuming app)
  private callbacks: ArCallbacks = {};
  private deviceConfig: ArDeviceConfig = { isDesktop: false, browser: 'Unknown' };
  private bypassConfig: ArBypassConfig = {};

  // Internal state
  keepVisible = false;

  // Marker detection state tracking (for debug logging)
  private previousMarkerVisible: boolean | null | undefined = null;

  constructor() {
    this.window = this.document.defaultView as Window & typeof globalThis;
  }

  /**
   * Set callbacks for AR lifecycle events
   * Decouples ArService from app-specific state management
   */
  setCallbacks(callbacks: ArCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Set device configuration for AR
   * Optional - defaults to non-desktop, unknown browser
   * @param config.containerElement - Optional container for AR video in Portal preview mode
   */
  setDeviceConfig(config: ArDeviceConfig): void {
    this.deviceConfig = config;
  }

  /**
   * Set bypass configuration
   * Optional - for testing and development
   */
  setBypassConfig(config: ArBypassConfig): void {
    this.bypassConfig = { ...this.bypassConfig, ...config };
  }

  /**
   * Wait for video element to be ready for AR tracking
   * Uses event-driven approach instead of arbitrary timeout
   * @returns Promise that resolves when video is playing, rejects after 5 seconds
   */
  private waitForVideoReady(video: HTMLVideoElement): Promise<void> {
    return new Promise((resolve, reject) => {
      // If video is already playing, resolve immediately
      if (video.readyState >= 3 && !video.paused) {
        console.log('[ArService] Video already ready');
        resolve();
        return;
      }

      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          // Clean up both listeners so they don't dangle on the video element
          video.removeEventListener('playing', onPlaying);
          video.removeEventListener('canplay', onCanPlay);
          reject(new Error('Video ready timeout after 5 seconds'));
        }
      }, 5000);

      // Listen for playing event
      const onPlaying = () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          video.removeEventListener('playing', onPlaying);
          video.removeEventListener('canplay', onCanPlay);
          console.log('[ArService] Video playing event received');
          resolve();
        }
      };

      // Listen for canplay event as backup
      const onCanPlay = () => {
        if (!resolved && video.readyState >= 3) {
          resolved = true;
          clearTimeout(timeout);
          video.removeEventListener('canplay', onCanPlay);
          video.removeEventListener('playing', onPlaying);
          console.log('[ArService] Video canplay event received');
          resolve();
        }
      };

      video.addEventListener('playing', onPlaying);
      video.addEventListener('canplay', onCanPlay);
    });
  }

  /**
   * Determines if AR should be bypassed based on multiple intelligent conditions
   */
  get shouldBypassAR(): boolean {
    // Check force bypass from config
    if (this.bypassConfig.forceBypass) {
      return true;
    }

    // First check if URL explicitly disables bypass
    if (this.hasExplicitBypassDisabled()) {
      console.log('🔧 AR bypass explicitly disabled via URL parameter');
      return false;
    }

    return this.hasARBypassFlag() || this.isTestingCampaign() || this.hasTestingUserAgent();
  }

  /**
   * Check if we're in development environment
   */
  private isDevelopmentEnvironment(): boolean {
    // Check for development indicators
    const isLocalhost = this.window.location.hostname === 'localhost' ||
                       this.window.location.hostname === '127.0.0.1';
    const hasDevPort = ['3000', '4200', '5173', '8080'].includes(this.window.location.port);
    const hasDebugParam = this.window.location.search.includes('debug=true');

    return isLocalhost || hasDevPort || hasDebugParam;
  }

  /**
   * Check if URL explicitly disables bypass (overrides all other bypass methods)
   */
  private hasExplicitBypassDisabled(): boolean {
    const urlParams = new URLSearchParams(this.window.location.search);
    const explicitDisable = urlParams.get('arBypass') === 'false';

    // If explicitly disabled via URL, clear localStorage flag
    if (explicitDisable) {
      try {
        this.window.localStorage.removeItem('arBypass');
      } catch (e) {
        console.warn('Failed to remove arBypass from localStorage:', e);
      }
    }

    return explicitDisable;
  }

  /**
   * Check for explicit AR bypass URL parameter
   * SECURITY: Only works in development environment
   * SIMPLIFIED: Now only accepts arBypass=true for clarity
   */
  private hasARBypassFlag(): boolean {
    // Security check - URL bypass only works in development
    if (!this.bypassConfig.enableDevBypass && !this.isDevelopmentEnvironment()) {
      return false;
    }

    // Check URL params first
    const urlParams = new URLSearchParams(this.window.location.search);
    const urlBypass = urlParams.get('arBypass') === 'true';

    // If found in URL, store in localStorage for persistence after query param clearing
    if (urlBypass) {
      try {
        this.window.localStorage.setItem('arBypass', 'true');
      } catch (e) {
        console.warn('Failed to store arBypass in localStorage:', e);
      }
      return true;
    }

    // Check localStorage for persisted bypass flag
    try {
      return this.window.localStorage.getItem('arBypass') === 'true';
    } catch (e) {
      console.warn('Failed to read arBypass from localStorage:', e);
      return false;
    }
  }

  /**
   * Check if current campaign is a testing/demo campaign
   * SECURITY: Only works in development environment
   */
  private isTestingCampaign(): boolean {
    // Security check - testing campaign bypass only works in development
    if (!this.bypassConfig.enableDevBypass && !this.isDevelopmentEnvironment()) {
      return false;
    }

    const short = this.bypassConfig.campaignShortCode;
    if (!short) return false;

    // Testing patterns
    const testingPatterns = [
      /^test-/,     // test-*
      /^demo-/,     // demo-*
      /^dev-/,      // dev-*
      /^local-/,    // local-*
      /^staging-/,  // staging-*
      /^default$/,  // our current default testing campaign
    ];

    return testingPatterns.some(pattern => pattern.test(short));
  }

  /**
   * Check for automated testing user agents (Cypress, Puppeteer, etc.)
   * SECURITY: Only works in development environment
   */
  private hasTestingUserAgent(): boolean {
    // Security check - user agent bypass only works in development
    if (!this.bypassConfig.enableDevBypass && !this.isDevelopmentEnvironment()) {
      return false;
    }

    const userAgent = this.window.navigator.userAgent.toLowerCase();
    const testingAgents = ['cypress', 'puppeteer', 'playwright', 'selenium', 'headless'];

    return testingAgents.some(agent => userAgent.includes(agent));
  }

  /**
   * Force AR bypass for programmatic testing
   * Useful for E2E tests that need to control AR behavior
   */
  forceARBypass(bypass = true): void {
    this.keepVisible = bypass;
    if (bypass) {
      console.log('🔧 AR bypass forced programmatically');
    }
  }

  /**
   * Check if AR is currently being bypassed
   */
  get isARBypassed(): boolean {
    return this.shouldBypassAR || this.keepVisible;
  }

  /**
   * Debug method to show current AR bypass status
   */
  logARStatus(): void {
    console.log('🔧 AR Status:', {
      shouldBypassAR: this.shouldBypassAR,
      keepVisible: this.keepVisible,
      isARBypassed: this.isARBypassed
    });
  }

  initAR(scene: Scene, camera: NgtCameraManual, renderer: WebGLRenderer, options?: ArOptions): void {
    console.log('[ArService] initAR() called');

    // Capture a generation token for this specific init attempt.
    // Any async callbacks that belong to a previous (now-cancelled) attempt will see
    // a mismatched generation and abort without touching the camera.
    const myGeneration = ++this.initGeneration;

    // Always save params so visibilitychange recovery can re-invoke initAR
    this.lastInitParams = { scene, camera, renderer, options };

    // Register visibility listener once (guard prevents duplicate registrations)
    if (!this.visibilityChangeListener) {
      this.visibilityChangeListener = () => this.onVisibilityChange();
      this.document.addEventListener('visibilitychange', this.visibilityChangeListener);
    }

    // Check if we already have AR initialized - if so, just resume it
    const existingVideo = this.getArVideoElement();
    if (existingVideo && this.arToolkitSource && this.arToolkitContext) {
      console.log('[ArService] Reusing existing AR initialization');
      this.resumeExistingAR(scene, camera, renderer, options);
      return;
    }

    // First time initialization
    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;

    console.log('[ArService] Initializing AR.js with auto-detection');

    // Initialize AR.js source with MINIMAL configuration
    // Let AR.js handle resolution, orientation, and camera constraints automatically
    // This follows AR.js-threejs best practices and fixes aspect ratio issues
    this.arToolkitSource = new THREEx.ArToolkitSource({
      sourceType: 'webcam',
      // Use high 'ideal' values - AR.js will use whatever the device camera provides
      // These are suggestions, not hard constraints (camera provides natural resolution)
      sourceWidth: this.window.innerWidth > this.window.innerHeight ? 640 : 480,
      sourceHeight: this.window.innerWidth > this.window.innerHeight ? 480 : 640,
    });

    // Capture a local reference so the stale-callback can still clean up the stream even
    // after pause()/destroy() has cleared this.arToolkitSource.
    //
    // Root-cause of the "camera stays on after slider + toggle-off" bug:
    //   1. initAR() runs, arToolkitSource.init() is called → getUserMedia() starts
    //   2. pause() fires: stopCameraStream() removes #arjs-video from the DOM
    //      (srcObject is null at this point because getUserMedia hasn't resolved yet),
    //      then sets this.arToolkitSource = undefined.
    //   3. getUserMedia() resolves → AR.js sets video.srcObject = stream on the
    //      now-detached video element, THEN calls our success callback.
    //   4. Stale callback: this.initGeneration !== myGeneration → stale → calls
    //      stopCameraStream() → getElementById('arjs-video') → null (already removed!)
    //      → the MediaStream is never stopped → camera LED stays on.
    //
    // Fix: hold a local reference to the ArToolkitSource instance.  When the stale
    // callback fires, use capturedSource.domElement (still valid even if removed from DOM)
    // to reach the stream and stop its tracks.
    const capturedSource = this.arToolkitSource;

    capturedSource.init(() => {
      // Guard: if pause()/destroy() was called while getUserMedia was in-flight,
      // the generation will have been incremented and this callback is stale.
      if (this.initGeneration !== myGeneration) {
        console.log('[ArService] Stale init success callback — stopping acquired camera stream via captured source reference');
        // DO NOT use stopCameraStream() here — it queries #arjs-video by DOM id,
        // which was already removed by pause().  Instead, reach the stream directly
        // through the captured ArToolkitSource instance whose domElement still holds
        // the backing <video> element even after it was detached from the document.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const video = (capturedSource as any).domElement as HTMLVideoElement | undefined;
        if (video) {
          const stream = video.srcObject as MediaStream | null;
          if (stream) {
            stream.getTracks().forEach(t => {
              t.stop();
              console.log(`[ArService] Stopped stale camera track (captured): ${t.kind} (${t.label})`);
            });
            video.srcObject = null;
          }
          // Only remove from DOM if still attached (may already be detached)
          if (video.parentElement) { video.remove(); }
        }
        return;
      }

      console.log('[ArService] Camera initialized with natural resolution');
      this.initRetryCount = 0; // Successful init — reset retry counter

      // Apply container-specific positioning if provided
      this.applyVideoPositioning();

      // Wait for video to be ready, then trigger callbacks
      this.onCameraReady(myGeneration);
    }, (err: unknown) => {
      console.error('[ArService] Camera initialization failed:', err);

      // Check generation: don't retry if we've been paused/destroyed
      if (this.initGeneration !== myGeneration) {
        console.log('[ArService] Stale init error callback — not retrying (AR was stopped)');
        return;
      }

      if (this.initRetryCount < this.MAX_INIT_RETRIES) {
        this.initRetryCount++;
        // Exponential-ish backoff: 500 ms, 1 000 ms, 1 500 ms
        // Gives iOS time to release hardware after the permission-probe getUserMedia
        const delay = this.initRetryCount * 500;
        console.warn(`[ArService] Retrying camera init in ${delay}ms (attempt ${this.initRetryCount}/${this.MAX_INIT_RETRIES})`);

        // Clean up any partial initialisation before retrying
        this.stopCameraStream();
        this.arToolkitSource = undefined;
        this.disposeArContext();

        setTimeout(() => {
          // Check again: user might have toggled AR off during the backoff delay
          if (this.initGeneration !== myGeneration) {
            console.log('[ArService] Retry cancelled — AR was stopped during backoff delay');
            return;
          }
          if (this.lastInitParams) {
            const { scene, camera, renderer, options } = this.lastInitParams;
            this.initAR(scene, camera, renderer, options);
          }
        }, delay);
      } else {
        console.error(`[ArService] Camera init failed after ${this.MAX_INIT_RETRIES} attempts — giving up`);
        this.initRetryCount = 0;
        this.callbacks.onLoadingEnd?.();
      }
    });

    // Initialize AR.js context with marker detection settings
    // AR.js automatically detects orientation from video dimensions
    this.arToolkitContext = new THREEx.ArToolkitContext({
      cameraParametersUrl: 'assets/camera_para.dat',
      detectionMode: 'mono',
      patternRatio: options?.patternRatio || 0.75,
      // AR.js will auto-detect orientation - no manual intervention needed
    });

    this.arToolkitContext.init(() => {
      // Guard: context init is async — bail out if generation has advanced (pause/destroy called)
      if (this.initGeneration !== myGeneration) {
        console.log('[ArService] Stale context init callback — generation mismatch, aborting');
        return;
      }
      console.log('[ArService] AR context initialized with options:', options);
      if (this.arToolkitContext && this.camera) {
        // Copy projection matrix from AR.js (handles aspect ratio correctly)
        this.camera.projectionMatrix.copy(this.arToolkitContext.getProjectionMatrix());

        this.arToolkitContext.arController.orientation = 'landscape';
        this.arToolkitContext.arController.options.orientation = 'landscape';

        // Apply scene scale if specified
        if (options?.sceneScale !== undefined && this.scene) {
          this.scene.scale.setScalar(options.sceneScale);
          console.log('[ArService] Applied scene scale:', options.sceneScale);
        }

        // Initialize marker tracking with smoothing parameters
        new THREEx.ArMarkerControls(this.arToolkitContext, this.camera, {
          type: 'pattern',
          patternUrl: options?.patternUrl || 'assets/patt.hiro',
          changeMatrixMode: 'cameraTransformMatrix',
          size: options?.size || 1,
          smooth: true,
          smoothCount: options?.smoothCount || 5,
          smoothTolerance: options?.smoothTolerance || 0.01,
          smoothThreshold: options?.smoothThreshold || 2
        });

        this.callbacks.onLoadingEnd?.();
      }
    });

    // Handle window resize (updates projection matrix when viewport changes)
    // Guard prevents duplicate registrations on re-init after mobile recovery
    if (!this.resizeListener) {
      this.resizeListener = () => this.onWindowResize();
      this.window.addEventListener('resize', this.resizeListener);
    }
  }

  /**
   * Helper method for camera ready state.
   * Called after AR.js's getUserMedia succeeds and the video element exists.
   *
   * The `generation` parameter must match `this.initGeneration` — if it doesn't,
   * pause()/destroy() was called while the camera was starting up, and this
   * callback is stale.  We stop the stream immediately and return without
   * enabling the camera UI.
   */
  private onCameraReady(generation: number): void {
    if (this.initGeneration !== generation) {
      console.log('[ArService] onCameraReady: stale generation — stopping camera and aborting');
      this.stopCameraStream();
      return;
    }

    const video = this.getArVideoElement();
    if (!video) return;

    // Wait for video metadata to load before proceeding
    this.waitForVideoReady(video).then(() => {
      // Check generation again: pause() might have been called during the wait
      if (this.initGeneration !== generation) {
        console.log('[ArService] onCameraReady (post-wait): stale generation — stopping camera');
        this.stopCameraStream();
        return;
      }
      this.onWindowResize(); // Update projection matrix
      this.addCSSClass('camera-enabled');
      this.callbacks.onCameraEnabled?.(true);
      this.callbacks.onLoadingEnd?.();
    }).catch((err) => {
      if (this.initGeneration !== generation) {
        this.stopCameraStream();
        return;
      }
      console.warn('[ArService] Video ready timeout, proceeding anyway:', err);
      this.onWindowResize();
      this.addCSSClass('camera-enabled');
      this.callbacks.onCameraEnabled?.(true);
      this.callbacks.onLoadingEnd?.();
    });
  }

  /**
   * Resume existing AR initialization when component remounts
   * Reuses existing video stream and AR context
   */
  private resumeExistingAR(scene: Scene, camera: NgtCameraManual, renderer: WebGLRenderer, options?: ArOptions): void {
    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;

    // Update projection matrix from existing AR context
    if (this.camera && this.arToolkitContext) {
      this.camera.projectionMatrix.copy(this.arToolkitContext.getProjectionMatrix());
    }

    // Apply scene scale
    if (options?.sceneScale !== undefined && this.scene) {
      this.scene.scale.setScalar(options.sceneScale);
    }

    // Resume video playback if paused
    const existingVideo = this.getArVideoElement();
    if (existingVideo) {
      if (existingVideo.paused) {
        existingVideo.play();
      }
      existingVideo.style.display = 'block';
    }
    this.addCSSClass('camera-enabled');
    this.callbacks.onCameraEnabled?.(true);
    this.callbacks.onLoadingEnd?.();
  }

  /**
   * Apply AR bypass logic globally (for use outside of AR context)
   * This allows AR bypass to work on any page, not just when AR is active
   */
  applyGlobalBypass(): void {
    // Cache once — shouldBypassAR evaluates all sub-checks internally
    const isBypassed = this.shouldBypassAR;
    if (isBypassed) {
      console.log('🔧 GLOBAL AR BYPASS ACTIVE - Adding force-scene-visible class');
      this.addCSSClass('force-scene-visible');
      this.removeCSSClass('scene-visible');
      this.callbacks.onSceneVisible?.(true);

      if (this.bypassConfig.forceBypass) {
        console.log('🔧 AR bypassed: forceBypass flag set programmatically');
      } else if (this.hasARBypassFlag()) {
        console.log('🔧 AR bypassed: URL parameter detected', this.window.location.search);
      } else if (this.isTestingCampaign()) {
        console.log('🔧 AR bypassed: Testing campaign pattern detected');
      } else if (this.hasTestingUserAgent()) {
        console.log('🔧 AR bypassed: Automated testing user agent detected');
      }
    } else {
      console.log('🔧 GLOBAL AR BYPASS INACTIVE - Removing force-scene-visible class');
      this.removeCSSClass('force-scene-visible');
      this.callbacks.onSceneVisible?.(false);
    }
  }

  injectBeforeRender(): void {
    // Update AR tracking (this must be called every frame)
    this.arToolkitContext?.update(this.arToolkitSource?.domElement);

    if (this.shouldBypassAR) {
      this.addCSSClass('force-scene-visible');
      this.removeCSSClass('scene-visible');
      this.callbacks.onSceneVisible?.(true);
    } else {
      this.removeCSSClass('force-scene-visible');
      const isVisible = this.keepVisible || this.camera?.visible;

      // Debug: Log marker detection state changes only
      if (this.previousMarkerVisible !== isVisible) {
        if (isVisible) {
          console.log('✅ [ArService] Marker DETECTED - Scene visible');
          console.log('   Camera visible:', this.camera?.visible);
          console.log('   Keep visible:', this.keepVisible);
        } else {
          console.log('❌ [ArService] Marker LOST - Scene hidden');
        }
        this.previousMarkerVisible = isVisible;
      }

      if (isVisible) {
        this.addCSSClass('scene-visible');
        this.callbacks.onSceneVisible?.(true);
      } else {
        this.removeCSSClass('scene-visible');
        this.callbacks.onSceneVisible?.(false);
      }
    }
  }

  private onWindowResize(): void {
    if (!this.arToolkitContext) return;

    const unsupported = !this.deviceConfig.isDesktop &&
                       (this.window.innerWidth > (this.window.innerHeight * 1.25));

    if (unsupported) {
      this.addCSSClass('unsupported-orientation');
      this.callbacks.onUnsupportedOrientation?.(true);
    } else {
      this.removeCSSClass('unsupported-orientation');
      this.callbacks.onUnsupportedOrientation?.(false);
    }

    if (this.arToolkitSource) {
      this.arToolkitSource.onResizeElement();

      if (this.arToolkitContext.arController !== null) {
        this.arToolkitSource.copyElementSizeTo(this.arToolkitContext.arController.canvas);
      }
    }

    this.camera?.updateProjectionMatrix();
  }

  /**
   * Pause AR tracking and release the camera.
   *
   * Stops the camera stream so the browser "in use" indicator turns off and
   * other apps can access the webcam. `arToolkitSource` is also cleared so
   * the next `initAR` call performs a full re-initialisation rather than
   * trying to resume a dead stream.
   */
  pause(): void {
    console.log('[ArService] Pausing AR and releasing camera');

    // Advance the generation counter FIRST — this immediately invalidates any
    // in-flight getUserMedia / onCameraReady callbacks so they won't re-enable
    // the camera after we've already stopped it.
    this.initGeneration++;
    // Also reset retry counter so a pending retry setTimeout won't fire.
    this.initRetryCount = 0;

    this.stopCameraStream();

    // Nullify source so initAR() re-initialises fully on next call.
    // Also dispose and clear the AR context — it will be recreated on next initAR().
    this.arToolkitSource = undefined;
    this.disposeArContext();

    this.removeCSSClass('camera-enabled');
    this.callbacks.onCameraEnabled?.(false);
  }

  /**
   * Apply a new scene scale to the running AR scene without reinitializing.
   * Called by GameEngineComponent when arOptions.sceneScale changes while AR is
   * already active (e.g. the user moves the Scene Scale slider in the Portal).
   * This avoids destroying/recreating the entire GameEngine — and the associated
   * getUserMedia() calls — for a single scalar change.
   */
  updateSceneScale(scale: number): void {
    if (this.scene) {
      this.scene.scale.setScalar(scale);
      console.log('[ArService] Live scene scale update:', scale);
    }
    // Persist so visibility-recovery re-init also uses the new scale
    if (this.lastInitParams?.options) {
      this.lastInitParams.options.sceneScale = scale;
    }
  }

  /**
   * Completely destroy AR resources
   * Only call this when component is being destroyed, not when toggling AR off
   */
  destroy(): void {
    console.log('[ArService] Destroying AR resources');

    // Advance the generation counter FIRST — same as pause(), this immediately
    // invalidates any in-flight getUserMedia / onCameraReady callbacks.
    this.initGeneration++;

    // Remove event listeners before tearing down
    if (this.visibilityChangeListener) {
      this.document.removeEventListener('visibilitychange', this.visibilityChangeListener);
      this.visibilityChangeListener = undefined;
    }
    if (this.resizeListener) {
      this.window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = undefined;
    }
    this.lastInitParams = undefined;
    this.initRetryCount = 0;

    // scene and renderer are owned by the angular-three canvas store — don't touch their
    // internals (no scene.clear(), no renderer.dispose()). Just drop our references.
    this.camera = undefined;
    this.scene = undefined;
    this.renderer = undefined;
    this.keepVisible = false;

    // Stop the camera stream and remove the video element
    this.stopCameraStream();
    this.arToolkitSource = undefined;

    // Clear every AR state class from <html> — prevents bleed into subsequent routes
    this.removeCSSClass('camera-enabled');
    this.removeCSSClass('scene-visible');
    this.removeCSSClass('force-scene-visible');
    this.removeCSSClass('unsupported-orientation');
    this.callbacks.onCameraEnabled?.(false);

    // Dispose AR Context
    this.disposeArContext();
  }

  // ============================================================================
  // Private Helper Methods
  // ============================================================================

  /**
   * Handles tab/app switching on mobile devices.
   *
   * Mobile browsers (iOS Safari, Chrome on Android) terminate the camera stream
   * at the OS level when the page is hidden. On return, the stream tracks are in
   * `readyState === 'ended'` and AR never recovers without explicit handling.
   *
   * Strategy:
   * - Hidden  → pause the video (stream may survive short switches on desktop)
   * - Visible → if any track is ended, perform a full re-init (new getUserMedia);
   *             otherwise just resume video.play() (stream survived the switch)
   */
  private onVisibilityChange(): void {
    // Single DOM query shared by both hidden and visible branches
    const video = this.getArVideoElement();

    if (this.document.visibilityState === 'hidden') {
      // Stop the MediaStream tracks so the OS camera indicator turns off when
      // the user switches tabs or minimises the browser.  Merely pausing the
      // video element does not release the hardware — the tracks must be
      // stopped explicitly.  We intentionally leave the video element in the
      // DOM (no video.remove()) so that the visibility-visible branch below
      // can still find it and take the streamIsDead → full re-init path.
      const stream = video?.srcObject as MediaStream | null;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        if (video) video.srcObject = null;
        console.log('[ArService] Page hidden — camera tracks stopped');
      } else {
        video?.pause();
        console.log('[ArService] Page hidden — video paused (no active stream)');
      }
      return;
    }

    // Page became visible again
    if (!video) return;

    const stream = video.srcObject as MediaStream | null;
    const streamIsDead = !stream ||
      stream.getTracks().length === 0 ||
      stream.getTracks().some(t => t.readyState === 'ended');

    if (streamIsDead) {
      console.log('[ArService] Page visible — camera stream is dead, performing full re-init');
      // Stop the dead stream cleanly, then re-run initAR with the saved params
      this.stopCameraStream();
      this.arToolkitSource = undefined;
      this.disposeArContext();

      if (this.lastInitParams) {
        const { scene, camera, renderer, options } = this.lastInitParams;
        this.callbacks.onLoadingStart?.();
        this.initAR(scene, camera, renderer, options);
      }
    } else {
      // Stream is still alive (e.g. quick desktop tab switch)
      console.log('[ArService] Page visible — stream still live, resuming video');
      video.play().catch(err =>
        console.warn('[ArService] video.play() failed on resume:', err)
      );
    }
  }


  /**
   * Stop all tracks on the active camera stream and remove the AR video element.
   *
   * AR.js attaches the MediaStream directly to the `arjs-video` element's
   * `srcObject` — the service never holds a reference to it directly, so we
   * must read it from the DOM. Stopping the tracks releases the webcam
   * immediately and clears the browser "camera in use" indicator.
   */
  private stopCameraStream(): void {
    const video = this.getArVideoElement();
    if (video) {
      // Stop every track on the active stream before removing the element
      const stream = video.srcObject as MediaStream | null;
      if (stream) {
        stream.getTracks().forEach(track => {
          track.stop();
          console.log(`[ArService] Stopped camera track: ${track.kind} (${track.label})`);
        });
        video.srcObject = null;
      }
      video.remove();
    }
  }

  /**
   * Apply video positioning to container
   * Both Portal and Experience apps provide explicit containers for AR video
   * Video is positioned absolutely within the provided container
   */
  private applyVideoPositioning(): void {
    const video = this.document.getElementById('arjs-video') as HTMLVideoElement;
    if (!video) return;

    const container = this.deviceConfig.containerElement;

    if (container) {
      // Verify container is valid and in DOM
      if (container.parentElement && this.document.contains(container)) {
        // Position video absolutely within container
        video.style.position = 'absolute';
        video.style.left = '0';
        video.style.top = '0';
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'cover';
        video.style.zIndex = '-1'; // Behind 3D canvas

        // Move video into container
        container.style.position = 'relative';
        container.appendChild(video);
      } else {
        console.warn('[ArService] Container provided but not valid, using default positioning');
        this.applyDefaultPositioning(video);
      }
    } else {
      // No container provided - use default positioning
      this.applyDefaultPositioning(video);
    }
  }

  /**
   * Apply default video positioning when no container provided
   * Full screen fixed positioning, no DOM manipulation
   */
  private applyDefaultPositioning(video: HTMLVideoElement): void {
    // Don't move video element - leave it where AR.js created it
    // Just apply full-screen fixed positioning
    video.style.position = 'fixed';
    video.style.left = '0';
    video.style.top = '0';
    video.style.width = '100vw';
    video.style.height = '100vh';
    video.style.objectFit = 'cover';
    video.style.zIndex = '-999'; // Behind all UI
  }

  /**
   * Dispose the AR toolkit context and clear the reference.
   * Extracted to avoid repeating the identical teardown block across
   * pause(), destroy(), onVisibilityChange(), and the camera-init retry callback.
   */
  private disposeArContext(): void {
    if (this.arToolkitContext?.arController?.cameraParam?.dispose) {
      this.arToolkitContext.arController.cameraParam.dispose();
    }
    if (this.arToolkitContext?.arController?.dispose) {
      this.arToolkitContext.arController.dispose();
    }
    this.arToolkitContext = undefined;
  }

  /**
   * Look up the AR.js video element in the current document.
   * AR.js always creates this element with id="arjs-video".
   * Centralising the query avoids repeating the cast at every call site.
   */
  private getArVideoElement(): HTMLVideoElement | null {
    return this.document.getElementById('arjs-video') as HTMLVideoElement | null;
  }

  /**
   * Add CSS class to document element
   */
  private addCSSClass(className: string): void {
    if (className && !this.document.documentElement.classList.contains(className)) {
      this.document.documentElement.classList.add(className);
    }
  }

  /**
   * Remove CSS class from document element
   */
  private removeCSSClass(className: string): void {
    if (className) {
      this.document.documentElement.classList.remove(className);
    }
  }
}
